import { useContext, useMemo, useState, useRef, useEffect, type FC } from 'react';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import axios from 'axios';
import { sanitizeHref } from '@wix/da-url';
import { useTranslation } from 'react-i18next';
import { PapiVideoCreatorGeneration } from '@wix/da-papi-types';
import { DATE_FORMATS, formatDate } from '@wix/da-i18next/pkg/time/functions';
import { getModelPropertiesById } from '../../ModelProperties';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import VideoSound from '@wix/da-ds/pkg/Icons/12x12/VideoSound';
import DownloadIcon from '@wix/da-ds/pkg/Icons/24x24/Download';
import StudioStashIcon from '@wix/da-ds/pkg/Icons/24x24/StudioStash';
import CopyIcon from '@wix/da-ds/pkg/Icons/16x16/Copy';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import UpgradeToCoreModal from '../../../../modals/UpgradeToCoreModal';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { useCopyToClipboard } from '@wix/da-shared-react/pkg/utils/hooks/useCopyToClipboard';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import { BiData, VideoCreatorVideoActionClickBiEvent } from '@wix/da-bi/pkg/events';
import VideoCreatorModeration from '../VideoCreatorModeration';
import DeviationMediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';
import { saveVideo } from '../../../../actions/studio/videoCreator';
import ddt from '@wix/da-ddt';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useIsNarrowViewport } from '../../../../hooks/useIsNarrowViewport';

import s from './VideoFile.scss';
import sParent from '../VideoCreatorFiles.scss';

const DOWNLOAD_FILE_NAME_MAX_LENGTH = 15;
const MAX_BLOB_SIZE = 100 * 1024 * 1024; // 100MB in bytes

export interface Props {
  videoGeneration: PapiVideoCreatorGeneration;
}

const VideoFile: FC<Props> = ({ videoGeneration }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useContext(MobileContext);
  const isNarrow = useIsNarrowViewport();
  const dispatch = useDispatch();
  const { hasCoreMembership } = useContext(SessionContext);
  const [isUpgradeToCoreModalOpen, setIsUpgradeToCoreModalOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoUrl = videoGeneration.generatedContent?.url;
  const wasModerated = videoGeneration.status === 'rejected';
  const genericError = videoGeneration.status === 'errored' || !videoUrl;

  const allOK = !wasModerated && !genericError;
  const referenceDeviation = videoGeneration.referenceDeviation;
  const referenceDeviationThumb = referenceDeviation
    ? DeviationMediaService.getSmallestThumb(referenceDeviation)
    : undefined;

  const model = useMemo(() => {
    return getModelPropertiesById(videoGeneration.modelId);
  }, [videoGeneration.modelId]);

  const copyPromptToClipboard = useCopyToClipboard(videoGeneration.prompt || '');

  const downloadFile = async () => {
    const url = videoGeneration.generatedContent?.url;
    if (!url) return;

    const getFilename = () => {
      if (videoGeneration.prompt) {
        const promptPrefix = videoGeneration.prompt
          .slice(0, DOWNLOAD_FILE_NAME_MAX_LENGTH)
          .replace(/[<>:"/\\|?*]/g, '') // Remove invalid filename characters
          .trim();
        return promptPrefix ? `${promptPrefix}.mp4` : `video-${videoGeneration.generationId}.mp4`;
      }
      return `video-${videoGeneration.generationId}.mp4`;
    };

    const filename = getFilename();

    try {
      // Fetch the file as a blob to handle cross-origin downloads
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = response.data;
      if (blob.size > MAX_BLOB_SIZE) {
        ddt.alert(
          'videoCreator',
          'Video file is larger than we designed for',
          ['videoCreator', 'fileSize'],
          {
            modelId: videoGeneration.modelId,
            blobSize: blob.size,
            generationId: videoGeneration.generationId,
          }
        );
      }

      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      // Fallback to direct download if fetch fails, because it is cross-origin this will
      // open the file in a new tab
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.target = '_blank';

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const saveToDrafts = () => {
    dispatch(saveVideo(videoGeneration.generationId));
  };

  // Mobile browsers ignore autoPlay. This will force them.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl || !allOK) return;
    const promise = video.play();
    if (promise !== undefined) {
      promise.catch(() => {
        // Ignore, the user probably has autoplay disabled
      });
    }
  }, [videoUrl, allOK]);

  return (
    <div
      className={cn(
        s['root'],
        sParent['file-item'],
        isNarrow && s['narrow'],
        isMobile && s['mobile']
      )}
    >
      <div className={s['preview']}>
        {/* Legal wants the moderation message shown for all errors for now */}
        {(wasModerated || genericError) && (
          <VideoCreatorModeration
            className={s['moderated']}
            companyName={model ? t(`studio.videoCreator.companyName.${model.type}`) : ''}
          />
        )}
        {allOK && (
          <video ref={videoRef} autoPlay loop muted playsInline width="100%" height="319">
            <source src={videoUrl} type="video/mp4" />
          </video>
        )}
      </div>
      <div className={s['details']}>
        {referenceDeviationThumb && (
          <div className={s['reference-thumb']}>
            <BiLink
              href={sanitizeHref(referenceDeviation.url)}
              target="_blank"
              rel="noopener noreferrer"
              biData={BiData<VideoCreatorVideoActionClickBiEvent>({
                evid: 951,
                generationid: videoGeneration.generationId,
                action: 'starting_image_thumb',
                is_valid: allOK,
              })}
            >
              <img
                className={s['thumb-image']}
                src={referenceDeviationThumb.src}
                alt={referenceDeviation.title ?? ''}
                title={referenceDeviation.title ?? ''}
              />
            </BiLink>
          </div>
        )}
        <div className={cn(s['prompt'], referenceDeviationThumb && s['has-reference-thumb'])}>
          <span className={s['prompt-text']}>{videoGeneration.prompt}</span>
          <IconButton
            className={s['copy-icon']}
            icon={CopyIcon}
            size={'small'}
            title={t('studio.videoCreator.files.copy')}
            aria-label={t('studio.videoCreator.files.copy')}
            onClick={copyPromptToClipboard}
            biData={BiData<VideoCreatorVideoActionClickBiEvent>({
              evid: 951,
              generationid: videoGeneration.generationId,
              action: 'prompt_copy',
              is_valid: allOK,
            })}
          />
        </div>
        <div className={s['properties']}>
          {model && (
            <div className={cn(s['model-name'], s['property'])}>
              {t(`studio.videoCreator.modelName.${model.type}`)}
            </div>
          )}
          <div className={cn(s['aspect-ratio'], s['property'])}>{videoGeneration.aspectRatio}</div>
          <div className={cn(s['duration'], s['property'])}>{videoGeneration.duration}s</div>
          {videoGeneration.sound && (
            <div className={cn(s['sound'], s['property'])}>
              <IconButton
                icon={VideoSound}
                size={'small'}
                title={t('studio.videoCreator.label.sound')}
                aria-label={t('studio.videoCreator.label.sound')}
                className={s['sound-icon']}
              />
            </div>
          )}
        </div>

        {allOK && (
          <div className={s['file-buttons']}>
            <div className={s['download']}>
              <TextButton
                className={s['download-button']}
                variant="main"
                size="small"
                onClick={() => {
                  void downloadFile();
                }}
                biData={BiData<VideoCreatorVideoActionClickBiEvent>({
                  evid: 951,
                  generationid: videoGeneration.generationId,
                  action: 'download_video',
                  is_valid: allOK,
                })}
              >
                <DownloadIcon
                  size={IconSize.SMALL}
                  disableDefaultColors
                  className={s['download-icon']}
                />
                {t('studio.videoCreator.files.download')}
              </TextButton>
            </div>
            <div className={s['save']}>
              <TextButton
                className={s['save-button']}
                variant="main"
                size="small"
                onClick={() => {
                  if (hasCoreMembership) {
                    saveToDrafts();
                  } else {
                    setIsUpgradeToCoreModalOpen(true);
                  }
                }}
                biData={BiData<VideoCreatorVideoActionClickBiEvent>({
                  evid: 951,
                  generationid: videoGeneration.generationId,
                  action: 'save_to_drafts',
                  is_valid: allOK,
                })}
              >
                <StudioStashIcon
                  size={IconSize.SMALL}
                  disableDefaultColors
                  className={s['save-icon']}
                />
                {t('studio.videoCreator.files.save')}
              </TextButton>
            </div>
          </div>
        )}

        <div className={s['created-at']}>
          <span className={s['created-date']}>
            {formatDate(videoGeneration.createdAt, DATE_FORMATS.Date.Short, i18n.language)}
          </span>
          <span className={s['created-time']}>
            {formatDate(videoGeneration.createdAt, DATE_FORMATS.Time.HourMinute, i18n.language)}
          </span>
        </div>
      </div>

      <UpgradeToCoreModal
        isOpen={isUpgradeToCoreModalOpen}
        onRequestClose={() => setIsUpgradeToCoreModalOpen(false)}
        onCancel={() => setIsUpgradeToCoreModalOpen(false)}
        onConfirm={() => setIsUpgradeToCoreModalOpen(false)}
      />
    </div>
  );
};

VideoFile.displayName = 'VideoFile';
export default VideoFile;
